class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    this.variants = this.parseJson(
      this.querySelector("[data-product-bundle-variants]")
    );
    this.variantsEl = this.querySelector('[data-bundle-variants="bundle"]');
    this.componentImageElements = [
      this.querySelector('[data-bundle-component-image="0"]'),
      this.querySelector('[data-bundle-component-image="1"]'),
    ];
    this.priceEl = this.querySelector('[data-bundle-price="bundle"]');
    this.variantInput = this.querySelector("[data-variant-id-input]");
    this.addBtn = this.querySelector("[data-product-bundle-add]");
    this.form = this.querySelector(".bundle-block__form--product-bundle");

    if (this.variantsEl) {
      this.variantsEl.addEventListener("change", () => this.handleVariantChange());
    }

    this.form?.addEventListener("submit", (e) => this.handleSubmit(e));

    this.handleVariantChange();
  }

  parseJson(el) {
    if (!el?.textContent) return [];
    try {
      return JSON.parse(el.textContent);
    } catch {
      return [];
    }
  }

  normOptionValue(s) {
    return String(s ?? "")
      .normalize("NFC")
      .trim()
      .toLowerCase();
  }

  findVariantFromOptions(variants, selectedValues) {
    if (!variants?.length || !selectedValues?.length) return null;
    return variants.find((v) => {
      const vOpts = [v.option1, v.option2, v.option3];
      for (let i = 0; i < selectedValues.length; i++) {
        if (
          this.normOptionValue(vOpts[i] ?? "") !==
          this.normOptionValue(selectedValues[i] ?? "")
        ) {
          return false;
        }
      }
      return true;
    });
  }

  getOptionRowsWithRadios(container) {
    const wrapper = container || this;
    return [...wrapper.querySelectorAll(".bundle-block__option")].filter((row) =>
      row.querySelector('input[type="radio"]')
    );
  }

  getSelectedOptionValues(container) {
    const wrapper = container || this;
    const rows = this.getOptionRowsWithRadios(wrapper);
    if (!rows.length) return [];

    rows.sort((a, b) => {
      const pa = parseInt(
        a.getAttribute("data-option-position") ||
          a.dataset?.optionPosition ||
          "0",
        10
      );
      const pb = parseInt(
        b.getAttribute("data-option-position") ||
          b.dataset?.optionPosition ||
          "0",
        10
      );
      return pa - pb;
    });

    return rows.map((row) => {
      const checked = row.querySelector('input[type="radio"]:checked');
      return checked ? String(checked.value ?? "").trim() : "";
    });
  }

  resolveVariant() {
    const container = this.variantsEl;
    if (!this.variants?.length) return null;

    const selected = this.getSelectedOptionValues(container);
    let variant = this.findVariantFromOptions(this.variants, selected);

    const optionGroupCount = container
      ? this.getOptionRowsWithRadios(container).length
      : 0;

    if (!variant && optionGroupCount === 1) {
      const checked = container?.querySelector(
        'input[type="radio"]:checked[data-variant-id]'
      );
      const vid = checked?.dataset?.variantId;
      if (vid != null) {
        variant = this.variants?.find((v) => String(v.id) === String(vid)) ?? null;
      }
    }

    if (!variant && this.variants.length === 1) {
      variant = this.variants[0];
    }
    return variant;
  }

  updateComponentImages() {
    const optionRows = this.getOptionRowsWithRadios(this.variantsEl);

    optionRows.forEach((optionRow) => {
      const componentIndex = parseInt(optionRow.getAttribute("data-component-index") ?? optionRow.dataset?.componentIndex ?? "", 10);
      if (Number.isNaN(componentIndex)) return;

      const checked = optionRow.querySelector("input:checked");
      const componentImageUrl = checked?.dataset?.componentImage;
      const imageContainer = this.componentImageElements[componentIndex];
      const imgEl = imageContainer?.querySelector("img");

      if (!componentImageUrl || !imgEl) return;

      imgEl.src = componentImageUrl;
      imgEl.srcset = componentImageUrl;
    });
  }

  updatePrice(variant) {
    if (!this.priceEl || !variant || !window.theme?.formatMoney) return;
    const format = window.theme.formatMoney;
    const mf = window.theme.moneyFormat;
    const free = window.theme.strings?.free || "Free";
    const priceHtml =
      variant.price === 0 ? free : format(variant.price, mf);
    const compareAt = variant.compare_at_price;
    const onSale =
      compareAt != null && Number(compareAt) > Number(variant.price);
    const compareHtml = onSale
      ? ` <span class="old-price">${format(compareAt, mf)}</span>`
      : "";
    this.priceEl.innerHTML = `<span class="new-price">${priceHtml}</span>${compareHtml}`;
    this.priceEl.classList.toggle("sale", onSale);
  }

  updateForm(variant) {
    if (!variant) return;
    if (this.variantInput) {
      this.variantInput.value = String(variant.id);
    }
    if (this.addBtn) {
      this.addBtn.disabled = !variant.available;
      const textEl = this.addBtn.querySelector(".btn__text");
      if (textEl) {
        textEl.textContent = variant.available
          ? window.theme?.strings?.addToCart || "Add to cart"
          : window.theme?.strings?.soldOut || "Sold out";
      }
    }
  }

  handleVariantChange() {
    this.updateComponentImages();

    const variant = this.resolveVariant();
    if (!variant) return;
    this.updatePrice(variant);
    this.updateForm(variant);
  }

  async handleSubmit(e) {
    e.preventDefault();
    const variant = this.resolveVariant();
    if (!variant?.available) {
      return;
    }
    const variantId =
      variant?.id ??
      this.variantInput?.value ??
      new FormData(this.form).get("id");
    if (!variantId) return;

    this.addBtn?.classList.add("is-loading");
    this.addBtn?.setAttribute("disabled", "disabled");

    try {
      const cartAddUrl = (
        window.theme?.routes?.cart_add_url || "/cart/add"
      ).replace(/\/add\/?$/, "/add.js");
      const res = await fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(String(variantId), 10), quantity: 1 }],
        }),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          `Cart API returned ${res.status}. Invalid JSON: ${text.slice(0, 80)}...`
        );
      }

      if (data.status) {
        throw new Error(data.description || "Add to cart failed");
      }

      document.dispatchEvent(
        new CustomEvent("theme:cart:refresh", { bubbles: true })
      );
      document.dispatchEvent(
        new CustomEvent("theme:product:added", { bubbles: true })
      );
    } catch (err) {
      console.error("Product bundle add to cart error:", err);
      window.publish?.(window.theme?.PUB_SUB_EVENTS?.cartError, {
        message: err.message,
      });
    } finally {
      this.addBtn?.classList.remove("is-loading");
      this.addBtn?.removeAttribute("disabled");
      const v = this.resolveVariant();
      if (v && this.addBtn) {
        this.addBtn.disabled = !v.available;
      }
    }
  }
}

customElements.define("product-bundle", ProductBundle);
